import { notFound } from "@tanstack/react-router";
import { getFeaturePageBacklinksQueryOptions } from "@/components/feature-page-backlinks/feature-page-backlinks-query";
import { getOutOfStockDealOffersQueryOptions } from "@/components/marketplace/deal-offers-query";
import { getReleasePriceStatsResourceQueryOptions } from "@/components/marketplace/price-stats-query";
import { getReleaseListingsQueryOptions } from "@/components/marketplace/release-listings-query";
import { getSpotifyAlbumQueryOptions } from "@/components/music/spotify-album-query";
import { getReleaseImagesResourceQueryOptions } from "@/components/release-images-query";
import { getWikipediaResourceQueryOptions } from "@/components/wikipedia-resource-query";
import { requireCatalogPageId } from "@/lib/catalog-route-param";
import { redirectCatalogRouteIfNeeded } from "@/utils/catalog-route-redirect";
import { resolveLegacyDiscogsCatalogId } from "@/utils/legacy-id-redirect";
import { prefetchForSsr } from "@/utils/loader";
import { getPrimaryFormatCategory } from "@garimpa/lib/formatClassification";
import { prefetchReleaseSeoOffer } from "./$id/prefetch-release-seo-offer";
import { getReleaseNotesResourceQueryOptions } from "./$id/release-notes-resource-query";
import { getReleaseQueryOptions } from "./$id/release-query";
import { getReleaseTrackVideosQueryOptions } from "./$id/release-track-videos-query";
type ReleaseSearch = {
  playing?: string;
};
interface ReleaseBreadcrumbInput {
  title: string;
  workId: string | null;
  workTitle: string | null;
  primaryIndex: {
    label: string;
    path: string;
  } | null;
  artists: Array<{
    id: string;
    name: string;
  }>;
}
const SplitLoader = async ({
  context: {
    queryClient,
    locale,
    viewer
  },
  params,
  location
}) => {
  const releaseId = requireCatalogPageId(params.id);

  // `locale` is part of the client's useQuery input — pre-fill the cache
  // under the same key so the component doesn't refetch on hydration.
  const release = await queryClient.ensureQueryData(getReleaseQueryOptions({
    id: releaseId,
    locale
  }));
  if (!release) {
    const legacyCatalogId = await resolveLegacyDiscogsCatalogId({
      entity: "release",
      id: releaseId,
      queryClient
    });
    if (legacyCatalogId) {
      redirectCatalogRouteIfNeeded({
        force: true,
        location,
        targetPath: `/release/${encodeURIComponent(legacyCatalogId)}`
      });
    }
    throw notFound();
  }
  redirectCatalogRouteIfNeeded({
    location,
    targetPath: `/release/${encodeURIComponent(releaseId)}`
  });
  const currency = viewer.preferences.currency;
  const offerFormat = getPrimaryFormatCategory({
    mediaCategories: release.mediaCategories,
    mediaFormats: release.mediaFormats
  }) ?? "all";
  const prefetches: Promise<unknown>[] = [queryClient.prefetchQuery(getReleaseNotesResourceQueryOptions({
    releaseId,
    locale
  })), queryClient.prefetchQuery(getReleaseTrackVideosQueryOptions(releaseId)), queryClient.prefetchQuery(getReleaseImagesResourceQueryOptions(releaseId)), queryClient.prefetchQuery(getReleaseListingsQueryOptions({
    releaseId,
    currency
  })), queryClient.prefetchQuery(getReleasePriceStatsResourceQueryOptions(releaseId, currency)), queryClient.prefetchQuery(getOutOfStockDealOffersQueryOptions({
    releaseId,
    currency
  }, {
    viewerId: viewer.userId
  })), queryClient.prefetchQuery(getFeaturePageBacklinksQueryOptions({
    entityType: "release",
    catalogId: releaseId,
    workId: release.workId ?? undefined,
    locale
  }))];
  if (release.workId) {
    prefetches.push(queryClient.prefetchQuery(getSpotifyAlbumQueryOptions({
      workId: release.workId
    })));
  }
  if (release.wikidataId) {
    prefetches.push(queryClient.prefetchQuery(getWikipediaResourceQueryOptions({
      wikidataId: release.wikidataId,
      locale
    })));
  }
  const [seoOffer] = await Promise.all([prefetchReleaseSeoOffer({
    queryClient,
    releaseId,
    workId: release.workId,
    currency,
    format: offerFormat,
    viewerId: viewer.userId
  }), prefetchForSsr(prefetches)]);
  return {
    release,
    seoOffer
  };
};
export { SplitLoader as loader };